import React, { useState, useMemo, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { IoCloudUploadOutline } from "react-icons/io5";
import { RiDeleteBin5Line } from "react-icons/ri";
import { TiTick } from "react-icons/ti";
import Modal from "../../components/common/modal";
import { useNavigate, useParams } from "react-router-dom";
import "../../assets/styles/tags.css";
import { useViewCategory } from "../../hooks/categorys/category.showall";
import { useViewIndustryQuery } from "../../hooks/industry/industryList";
import { useViewTagsQuery } from "../../hooks/tags/viewAllTags";
import { useProductStore } from "../../hooks/products/createProducts";
import { useProductUpdate } from "../../hooks/products/updateProducts";
import { useViewSingleProduct } from "../../hooks/products/viewSingleProduct";
import { toast } from "react-hot-toast";
import Toggle from "../../components/common/toggle";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanLimitMessage,
  getPlanMediaLimits,
  filterFilesForPlanLimit,
  formatPlanCap,
} from "../subscription/planLimits";
import {
  productValidationSchema,
  productEditValidationSchema,
} from "../../validation/productValidation";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;

const toChoiceId = (item) => {
  if (item === null || item === undefined) return "";
  if (typeof item === "string") return item.trim();
  if (typeof item === "object") {
    const id = item._id || item.id;
    return id ? String(id) : "";
  }
  return String(item);
};

const normalizeChoiceName = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  if (typeof item === "object") {
    return (item.title || item.name || item.label || "").trim();
  }
  return "";
};

const resolveChoiceId = (raw, optionsList = []) => {
  if (raw === null || raw === undefined) return "";

  if (Array.isArray(raw)) {
    return resolveChoiceId(raw[0], optionsList);
  }

  if (typeof raw === "object") {
    const id = toChoiceId(raw);
    if (id) return id;

    const name = normalizeChoiceName(raw);
    if (name) {
      const found = optionsList.find(
        (opt) =>
          normalizeChoiceName(opt).toLowerCase() === name.toLowerCase(),
      );
      if (found) return String(found._id || found.id);
    }
    return "";
  }

  const str = String(raw).trim();
  if (!str) return "";

  if (OBJECT_ID_REGEX.test(str)) return str;

  const found = optionsList.find(
    (opt) => normalizeChoiceName(opt).toLowerCase() === str.toLowerCase(),
  );
  if (found) return String(found._id || found.id);

  return str;
};

const extractCategoryRaw = (product) =>
  product?.category ??
  product?.categories ??
  product?.category_id ??
  product?.categoryId ??
  product?.category_info ??
  product?.categoryData ??
  product?.categoryName ??
  product?.category_name ??
  null;

const extractIndustryRaw = (product) =>
  product?.industries ??
  product?.industry ??
  product?.industry_id ??
  product?.industryId ??
  product?.industry_info ??
  product?.industryData ??
  product?.industryName ??
  product?.industry_name ??
  null;

const extractIndustryIds = (raw, optionsList = []) => {
  if (raw === null || raw === undefined) return [];

  const items = Array.isArray(raw) ? raw : [raw];
  return items
    .map((item) => resolveChoiceId(item, optionsList))
    .filter(Boolean);
};

const mergeChoiceOptions = (list, selectedItems = []) => {
  const map = new Map();

  (list || []).forEach((item) => {
    const id = toChoiceId(item);
    if (id) map.set(id, item);
  });

  (selectedItems || []).forEach((item) => {
    if (!item) return;
    const id = toChoiceId(item);
    const name = normalizeChoiceName(item);
    if (id && !map.has(id) && name) {
      map.set(id, typeof item === "object" ? item : { _id: id, title: name });
    }
  });

  return Array.from(map.values());
};

const AddProducts = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState();
  // "http://i.pravatar.cc/500?img=7"

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data: singleProductData, isLoading: isProductLoading } =
    useViewSingleProduct(id);

  const productToEdit = useMemo(() => {
    if (!singleProductData) return null;
    return (
      singleProductData?.data?.product ||
      singleProductData?.product ||
      singleProductData?.data ||
      singleProductData
    );
  }, [singleProductData]);

  const initialFormValues = useMemo(() => {
    const empty = {
      title: "",
      subTitle: "",
      description: "",
      category: "",
      industries: [],
      thumbnail: null,
      bulkImages: [],
    };

    if (isEditMode && productToEdit) {
      const thumb =
        productToEdit.thumbnail_image ||
        productToEdit.thumbnail ||
        productToEdit.image ||
        productToEdit.media?.thumbnail ||
        productToEdit.media?.thumbnail_image;

      let thumbnail = null;
      if (thumb) {
        thumbnail =
          typeof thumb === "string"
            ? thumb
            : thumb.url ||
              thumb.path ||
              thumb.image ||
              thumb.thumbnail_image ||
              thumb;
      }

      return {
        ...empty,
        title: productToEdit.title || "",
        subTitle: productToEdit.subTitle || "",
        description: productToEdit.description || "",
        category: resolveChoiceId(extractCategoryRaw(productToEdit)),
        industries: extractIndustryIds(extractIndustryRaw(productToEdit)),
        thumbnail,
      };
    }

    return empty;
  }, [isEditMode, productToEdit]);

  const [bulkImages, setBulkImages] = useState([]);
  const [uploadSave, setUploadSave] = useState(false);
  const [upload, setUload] = useState(false);
  const [bulkVideos, setBulkVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileType, setFileType] = useState("stone");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagNameById, setTagNameById] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const tagDropdownRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [bulkImageFiles, setBulkImageFiles] = useState([]);
  // const [catalogFiles, setCatalogFiles] = useState([]);
  const [bulkVideoFiles, setBulkVideoFiles] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [deletedVideoIds, setDeletedVideoIds] = useState([]);
  const [deletedCatalogIds, setDeletedCatalogIds] = useState([]);

  const [existingCatalogs, setExistingCatalogs] = useState([]);
  const [newCatalogFiles, setNewCatalogFiles] = useState([]);

  const { data: categoriesData } = useViewCategory();
  const { data: industriesData } = useViewIndustryQuery();
  const { data: tagsData } = useViewTagsQuery();
  const { mutate: createProduct, isLoading: isCreating } = useProductStore();
  const { data: subscriptionResponse, isPlaceholderData } = useViewSubscription();
  const planLimits = getPlanMediaLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });
  const { mutate: updateProduct, isLoading: isUpdating } = useProductUpdate();

  const extractMediaPath = (raw) => {
    if (!raw) return "";
    if (typeof raw === "string") return raw;

    const fileField = raw.file;
    const candidates = [
      raw.url,
      raw.path,
      typeof fileField === "string" ? fileField : null,
      fileField?.url,
      fileField?.path,
      raw.image,
      raw.thumbnail_image,
    ];

    return (
      candidates.find((value) => typeof value === "string" && value.trim()) ||
      ""
    );
  };

  const resolveMediaUrl = (raw) => resolveImageUrl(extractMediaPath(raw));

  const resolveVideoPayloadUrl = (file) => {
    if (!file || file instanceof File) return "";
    const path =
      typeof file === "string" ? file.trim() : extractMediaPath(file);
    if (!path) return "";
    return resolveImageUrl(path);
  };

  useEffect(() => {
    if (isEditMode && productToEdit) {
      const getPath = (raw) => {
        const path = extractMediaPath(raw);
        if (!path) return "";
        if (path.startsWith("http")) return path;
        if (path.startsWith("uploads")) return path;
        if (path.startsWith("/uploads")) return path.substring(1);
        return path;
      };

      // Handle Thumbnail
      const thumb =
        productToEdit.thumbnail_image ||
        productToEdit.thumbnail ||
        productToEdit.image ||
        productToEdit.media?.thumbnail ||
        productToEdit.media?.thumbnail_image;

      if (thumb) {
        setImagePreview(resolveMediaUrl(thumb));

        const thumbPath =
          typeof thumb === "string"
            ? thumb
            : thumb.url ||
              thumb.path ||
              thumb.image ||
              thumb.thumbnail_image ||
              "";

        setThumbnailFile(thumbPath);
      }

      // Handle Bulk Images
      const rawImages =
        productToEdit.images ||
        productToEdit.media?.images ||
        productToEdit.media?.image ||
        [];

      if (Array.isArray(rawImages)) {
        setBulkImages(rawImages.map((img) => resolveMediaUrl(img)));
        setBulkImageFiles(
          rawImages.map((img) => {
            const path = getPath(img);
            if (typeof img === "object" && (img._id || img.id)) {
              return { _id: img._id || img.id, url: path };
            }
            return path;
          }),
        );
      }

      // Handle Videos
      const rawVideos =
        productToEdit.videos || productToEdit.media?.videos || [];

      if (Array.isArray(rawVideos)) {
        const uniqueVideos = [];
        const uniqueVideoFiles = [];
        const seenUrls = new Set();
        const autoDeletedIds = [];

        rawVideos.forEach((vid) => {
          const videoPath = getPath(vid);
          if (!videoPath) return;

          if (!seenUrls.has(videoPath)) {
            seenUrls.add(videoPath);
            uniqueVideos.push(resolveMediaUrl(vid));
            uniqueVideoFiles.push({
              _id: typeof vid === "object" ? vid._id || vid.id : null,
              file: videoPath,
              title: typeof vid === "object" ? vid.title || "" : "",
              description:
                typeof vid === "object" ? vid.description || "" : "",
            });
          } else if (typeof vid === "object" && (vid._id || vid.id)) {
            autoDeletedIds.push(vid._id || vid.id);
          }
        });

        setBulkVideos(uniqueVideos);
        setBulkVideoFiles(uniqueVideoFiles);
        if (autoDeletedIds.length > 0) {
          setDeletedVideoIds((prev) => [
            ...new Set([...prev, ...autoDeletedIds]),
          ]);
        }
      }

      // Handle Catalogs
      const catalogs =
        productToEdit.catlogs ||
        productToEdit.catelogs ||
        productToEdit.catalogs ||
        productToEdit.media?.catlogs ||
        productToEdit.media?.catelogs ||
        productToEdit.media?.catalogs ||
        [];

      if (Array.isArray(catalogs)) {
        const initialUploadedFiles = catalogs.map((cat, index) => {
          const fileName =
            cat.title ||
            (typeof (cat.file || cat) === "string"
              ? (cat.file || cat).split("/").pop()
              : `File ${index + 1}`);
          return {
            id: cat._id || cat.id || index,
            name: fileName,
            size: "N/A",
            status: "uploaded",
          };
        });
        setUploadedFiles(initialUploadedFiles);
        setExistingCatalogs(catalogs);
      }

      // Handle Tags
      const rawTags = productToEdit.tags || productToEdit.media?.tags || [];
      if (Array.isArray(rawTags)) {
        const namesFromProduct = {};
        const tagIds = rawTags
          .map((tag) => {
            if (typeof tag === "string") return tag;
            const id = tag._id || tag.id;
            const name = tag.title || tag.name;
            if (id && name) {
              namesFromProduct[String(id)] = name;
            }
            return id || name || tag;
          })
          .filter(Boolean);

        setTagNameById((prev) => ({ ...prev, ...namesFromProduct }));
        setTags(tagIds);
      }
    }
  }, [isEditMode, productToEdit]);

  const categoriesList = useMemo(() => {
    if (!categoriesData) return [];
    const list =
      categoriesData?.data?.category ||
      categoriesData?.data?.categories ||
      categoriesData?.data ||
      categoriesData?.category ||
      categoriesData?.categories ||
      categoriesData ||
      [];
    return Array.isArray(list) ? list : [];
  }, [categoriesData]);

  const industriesList = useMemo(() => {
    if (!industriesData) return [];
    const list =
      industriesData?.data?.industry ||
      industriesData?.data?.industries ||
      industriesData?.data ||
      industriesData?.industry ||
      industriesData?.industries ||
      industriesData ||
      [];
    return Array.isArray(list) ? list : [];
  }, [industriesData]);

  const categoryOptions = useMemo(() => {
    const raw = productToEdit ? extractCategoryRaw(productToEdit) : null;
    const selected = raw ? (Array.isArray(raw) ? raw : [raw]) : [];
    return mergeChoiceOptions(categoriesList, selected);
  }, [categoriesList, productToEdit]);

  const industryOptions = useMemo(() => {
    const raw = productToEdit ? extractIndustryRaw(productToEdit) : null;
    const selected = raw ? (Array.isArray(raw) ? raw : [raw]) : [];
    return mergeChoiceOptions(industriesList, selected);
  }, [industriesList, productToEdit]);

  const availableTags = useMemo(() => {
    if (!tagsData) return [];
    const list = tagsData?.data || tagsData?.tags || tagsData || [];
    return Array.isArray(list) ? list : [];
  }, [tagsData]);

  useEffect(() => {
    if (!availableTags.length) return;

    setTagNameById((prev) => {
      const next = { ...prev };
      availableTags.forEach((tag) => {
        const id = tag._id || tag.id;
        const name = tag.title || tag.name;
        if (id && name) {
          next[String(id)] = name;
        }
      });
      return next;
    });
  }, [availableTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target)
      ) {
        setIsTagDropdownOpen(false);
      }
    };

    if (isTagDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTagDropdownOpen]);

  const getTagLabel = (tagRef) => {
    const tagKey = String(tagRef);
    if (tagNameById[tagKey]) return tagNameById[tagKey];

    const fromList = availableTags.find(
      (t) => String(t._id || t.id) === tagKey,
    );
    if (fromList) return fromList.title || fromList.name;

    const looksLikeObjectId = /^[a-f0-9]{24}$/i.test(tagKey);
    if (!looksLikeObjectId) return tagRef;

    return "";
  };

  const isTagSelected = (tagId) =>
    tags.some((selected) => String(selected) === String(tagId));

  const getEmbedUrl = (url) => {
    if (
      typeof url === "string" &&
      (url.includes("youtube.com") || url.includes("youtu.be"))
    ) {
      let videoId = "";
      if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleAddVideoUrl = (e) => {
    e.preventDefault();

    if (!videoUrl.trim()) return;
    if (
      Number.isFinite(planLimits.maxProductVideos) &&
      bulkVideos.length >= planLimits.maxProductVideos
    ) {
      toast.error(
        `Your plan allows ${planLimits.maxProductVideos} video(s) per product.`,
      );
      return;
    }

    setBulkVideos((prev) => [...prev, videoUrl]);
    setBulkVideoFiles((prev) => [
      ...prev,
      { file: videoUrl, title: "", description: "" },
    ]);
    setVideoUrl("");
  };

  const handleBulkVideoChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!picked.length) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: picked,
      currentCount: bulkVideoFiles.length,
      maxCount: planLimits.maxProductVideos,
      maxSizeMB: planLimits.maxProductVideoSizeMB,
      itemLabel: "video(s) per product",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    const newFiles = accepted.map((file) => ({
      file,
      title: "",
      description: "",
    }));
    setBulkVideoFiles((prev) => [...prev, ...newFiles]);
    const newPreviews = accepted.map((file) => URL.createObjectURL(file));
    setBulkVideos((prev) => [...prev, ...newPreviews]);
  };

  const handleVideoMetaChange = (index, field, value) => {
    setBulkVideoFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  const uploadImages = () => {
    setUload(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: isEditMode
      ? productEditValidationSchema
      : productValidationSchema,
    onSubmit: (values) => {
      if (!isEditMode && !planLimits.products.canCreate) {
        toast.error(getPlanLimitMessage("products", planLimits));
        return;
      }

      if (
        Number.isFinite(planLimits.maxProductImages) &&
        bulkImageFiles.length > planLimits.maxProductImages
      ) {
        toast.error(
          `Image limit exceeded. Your plan allows ${planLimits.maxProductImages} image(s) per product.`,
        );
        return;
      }
      if (
        Number.isFinite(planLimits.maxProductVideos) &&
        bulkVideoFiles.length > planLimits.maxProductVideos
      ) {
        toast.error(
          `Video limit exceeded. Your plan allows ${planLimits.maxProductVideos} video(s) per product.`,
        );
        return;
      }
      if (
        Number.isFinite(planLimits.maxProductCatalogs) &&
        existingCatalogs.length + newCatalogFiles.length >
          planLimits.maxProductCatalogs
      ) {
        toast.error(
          `Catalogue limit exceeded. Your plan allows ${planLimits.maxProductCatalogs} catalogue(s) per product.`,
        );
        return;
      }

      const body = new FormData();
      body.append("title", values.title);
      body.append("subTitle", values.subTitle);
      body.append("description", values.description);

      const categoryId =
        typeof values.category === "string"
          ? values.category.trim()
          : values.category?._id || values.category?.id || "";

      if (categoryId) {
        body.append("category[0]", categoryId);
      }

      values.industries
        .filter((indId) => indId && String(indId).trim())
        .forEach((indId, index) => {
          body.append(`industries[${index}]`, indId);
        });

    if (thumbnailFile instanceof File) {
      body.append("thumbnail_image", thumbnailFile);
    }

    bulkImageFiles.forEach((file, index) => {
      if (file instanceof File) {
        body.append("images", file);
      } else {
        const url = typeof file === "object" && file.url ? file.url : file;
        if (url) {
          body.append(`images[${index}][url]`, url);
        }
      }
    });

    // Keep existing catalogs
    existingCatalogs.forEach((cat) => {
      const existingPath =
        typeof cat === "string" ? cat : cat.url || cat.path || cat.file;

      if (existingPath) {
        body.append("existingCatlogs", existingPath);
      }
    });

    // Upload new catalogs
    newCatalogFiles.forEach((file) => {
      body.append("catlogs", file);
    });

    let videoIndex = 0;
    bulkVideoFiles.forEach((vidObj) => {
      const file = vidObj.file;
      const isNewFile = file instanceof File;
      const existingUrl = resolveVideoPayloadUrl(file);

      if (!isNewFile && !existingUrl) return;

      if (vidObj._id) {
        body.append(`videos[${videoIndex}][_id]`, vidObj._id);
      }
      body.append(`videos[${videoIndex}][title]`, vidObj.title || "");
      body.append(
        `videos[${videoIndex}][description]`,
        vidObj.description || "",
      );

      if (isNewFile) {
        body.append("videos", file);
      } else {
        body.append(`videos[${videoIndex}][url]`, existingUrl);
      }

      videoIndex += 1;
    });

    tags.forEach((tag) => {
      body.append("tags", tag);
    });

    // Send deleted media IDs so backend knows what to remove
    deletedImageIds.forEach((id) => body.append("deletedImageIds[]", id));
    deletedVideoIds.forEach((id) => body.append("deletedVideoIds[]", id));
    deletedCatalogIds.forEach((id) => body.append("deletedCatalogIds[]", id));

    if (isEditMode) {
      updateProduct(
        { id, body },
        {
          onSuccess: () => {
            toast.success("Product updated successfully!");
            navigate("/products");
          },
          onError: (err) => {
            toast.error(
              err?.response?.data?.message || "Failed to update product",
            );
          },
        },
      );
    } else {
      createProduct(body, {
        onSuccess: () => {
          toast.success("Product created successfully!");
          navigate("/products");
        },
        onError: (err) => {
          toast.error(
            err?.response?.data?.message || "Failed to create product",
          );
        },
      });
    }
    },
  });

  useEffect(() => {
    formik.setFieldValue("bulkImages", bulkImageFiles);
  }, [bulkImageFiles]);

  useEffect(() => {
    if (!isEditMode || !productToEdit) return;

    const categoryId = resolveChoiceId(
      extractCategoryRaw(productToEdit),
      categoriesList,
    );
    const industryIds = extractIndustryIds(
      extractIndustryRaw(productToEdit),
      industriesList,
    );

    if (categoryId && String(formik.values.category) !== categoryId) {
      formik.setFieldValue("category", categoryId);
    }

    if (
      industryIds.length > 0 &&
      String(formik.values.industries[0] || "") !== String(industryIds[0])
    ) {
      formik.setFieldValue("industries", industryIds);
    }
  }, [isEditMode, productToEdit, categoriesList, industriesList]);

  const handleBulkImageChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!picked.length) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: picked,
      currentCount: bulkImageFiles.length,
      maxCount: planLimits.maxProductImages,
      maxSizeMB: planLimits.maxProductImageSizeMB,
      itemLabel: "image(s) per product",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    const nextFiles = [...bulkImageFiles, ...accepted];
    setBulkImageFiles(nextFiles);
    const newImages = accepted.map((file) => URL.createObjectURL(file));
    setBulkImages((prev) => [...prev, ...newImages]);
    formik.setFieldValue("bulkImages", nextFiles);
    formik.setFieldTouched("bulkImages", true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: imageFiles,
      currentCount: bulkImageFiles.length,
      maxCount: planLimits.maxProductImages,
      maxSizeMB: planLimits.maxProductImageSizeMB,
      itemLabel: "image(s) per product",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    const nextFiles = [...bulkImageFiles, ...accepted];
    setBulkImageFiles(nextFiles);
    const newImages = accepted.map((file) => URL.createObjectURL(file));
    setBulkImages((prev) => [...prev, ...newImages]);
    formik.setFieldValue("bulkImages", nextFiles);
    formik.setFieldTouched("bulkImages", true);
  };

  const handleThumbnailChange = (input) => {
    if (input.files && input.files[0]) {
      const file = input.files[0];
      if (
        Number.isFinite(planLimits.maxProductImageSizeMB) &&
        planLimits.maxProductImageSizeMB > 0 &&
        file.size > planLimits.maxProductImageSizeMB * 1024 * 1024
      ) {
        toast.error(
          `Thumbnail is too large. Max size is ${planLimits.maxProductImageSizeMB}MB.`,
        );
        input.value = "";
        return;
      }
      setThumbnailFile(file);
      formik.setFieldValue("thumbnail", file);
      formik.setFieldTouched("thumbnail", true);
      const reader = new FileReader();
      reader.onload = function (e) {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = (indexToDelete) => {
    const imgToDel = bulkImageFiles[indexToDelete];
    if (typeof imgToDel === "object" && (imgToDel._id || imgToDel.id)) {
      setDeletedImageIds((prev) => [...prev, imgToDel._id || imgToDel.id]);
    } else if (typeof imgToDel === "string" && imgToDel.includes("uploads")) {
      // If we don't have an ID but have a path, we might need a different approach,
      // but usually existing ones have IDs in the state.
    }
    const nextFiles = bulkImageFiles.filter(
      (_, index) => index !== indexToDelete,
    );
    setBulkImages((prev) => prev.filter((_, index) => index !== indexToDelete));
    setBulkImageFiles(nextFiles);
    formik.setFieldValue("bulkImages", nextFiles);
    formik.setFieldTouched("bulkImages", true);
  };

  const handleImageClick = (img, index) => {
    setSelectedImage(img);
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setSelectedImageIndex(null);
  };

  const handleVideoClick = (vid, index) => {
    setSelectedVideo(vid);
    setSelectedVideoIndex(index);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
    setSelectedVideoIndex(null);
  };

  const handleDeleteVideo = (indexToDelete) => {
    const vidToDel = bulkVideoFiles[indexToDelete];
    // Check if it's an existing video with an ID (from getPath logic earlier)
    if (typeof vidToDel === "object" && (vidToDel._id || vidToDel.id)) {
      setDeletedVideoIds((prev) => [...prev, vidToDel._id || vidToDel.id]);
    }
    setBulkVideos((prev) => prev.filter((_, index) => index !== indexToDelete));
    setBulkVideoFiles((prev) =>
      prev.filter((_, index) => index !== indexToDelete),
    );
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim()) {
      // Find if this tag exists in availableTags to get its ID
      const existing = availableTags.find(
        (t) =>
          (t.title || t.name || "").toLowerCase() ===
          tagInput.trim().toLowerCase(),
      );

      if (existing) {
        const tagId = existing._id || existing.id;
        if (!tags.includes(tagId)) {
          setTags((prev) => [...prev, tagId]);
          setTagInput("");
        }
      } else {
        toast.error(
          "Tag not found. Please select from the list or add it in the Tags page.",
        );
      }
    }
  };

  const handleToggleTag = (tagId) => {
    const tagKey = String(tagId);

    if (isTagSelected(tagId)) {
      setTags((prev) => prev.filter((t) => String(t) !== tagKey));
      return;
    }

    const match = availableTags.find(
      (t) => String(t._id || t.id) === tagKey,
    );
    const name = match?.title || match?.name;
    if (name) {
      setTagNameById((prev) => ({ ...prev, [tagKey]: name }));
    }

    setTags((prev) => [...prev, tagId]);
  };

  const handleRemoveTag = (tagToRemove) => {
    const tagKey = String(tagToRemove);
    setTags((prev) => prev.filter((tag) => String(tag) !== tagKey));
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    );
    if (!validFiles.length) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: validFiles,
      currentCount: existingCatalogs.length + newCatalogFiles.length,
      maxCount: planLimits.maxProductCatalogs,
      maxSizeMB: planLimits.maxProductCatalogSizeMB,
      itemLabel: "catalogue(s) per product",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    setNewCatalogFiles((prev) => [...prev, ...accepted]);
    const newFiles = accepted.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(2),
      progress: 100,
      status: "uploaded",
      timeLeft: 0,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!picked.length) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: picked,
      currentCount: existingCatalogs.length + newCatalogFiles.length,
      maxCount: planLimits.maxProductCatalogs,
      maxSizeMB: planLimits.maxProductCatalogSizeMB,
      itemLabel: "catalogue(s) per product",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    setNewCatalogFiles((prev) => [...prev, ...accepted]);
    const newFiles = accepted.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(2),
      progress: 100,
      status: "uploaded",
      timeLeft: 0,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (id, index) => {
    const fileToDel = existingCatalogs[index] || newCatalogFiles[index];
    if (typeof fileToDel === "object" && (fileToDel._id || fileToDel.id)) {
      setDeletedCatalogIds((prev) => [...prev, fileToDel._id || fileToDel.id]);
    }
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
    if (index < existingCatalogs.length) {
      setExistingCatalogs((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - existingCatalogs.length;

      setNewCatalogFiles((prev) => prev.filter((_, i) => i !== newIndex));
    }
  };

  const handleRetryFile = (id) => {
    setUploadedFiles((prev) =>
      prev.map((file) =>
        file.id === id ? { ...file, status: "uploading", progress: 0 } : file,
      ),
    );
  };

  if (isEditMode && isProductLoading) {
    return (
      <div className="body">
        <p>Loading product...</p>
      </div>
    );
  }

  if (isEditMode && !isProductLoading && !productToEdit) {
    return (
      <div className="body">
        <p>Product not found.</p>
        <button
          type="button"
          className="CommonButton"
          onClick={() => navigate("/products")}
        >
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="body">
        <h2 style={{ marginBottom: "20px" }}>
          {isEditMode ? "Edit Product" : "Add Product"}
        </h2>
        {!isEditMode && (
          <p
            className={`plan-limit-hint${planLimits.products.canCreate ? "" : " is-limit"}`}
          >
            {getPlanLimitMessage("products", planLimits)}
          </p>
        )}

        <form onSubmit={formik.handleSubmit} noValidate>
        <div className="inputFields">
          <div className="formSection">
            <div className="form-header">
              <div
                className="text-inputs"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    placeholder="Text here..."
                    style={{ width: "100%", marginTop: "10px" }}
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <p className="error-text">{formik.errors.title}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="subTitle" className="label">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    id="subTitle"
                    name="subTitle"
                    placeholder="Text here..."
                    style={{ width: "100%", marginTop: "10px" }}
                    value={formik.values.subTitle}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
              <div className="image-upload-section">
                <div className="avatar-upload" style={{ margin: "0" }}>
                  <div className="avatar-edit">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      onChange={(e) => handleThumbnailChange(e.target)}
                      onBlur={() => formik.setFieldTouched("thumbnail", true)}
                    />
                    <label htmlFor="imageUpload"></label>
                  </div>
                  <div className="avatar-preview">
                    <div
                      id="imagePreview"
                      style={{ backgroundImage: `url(${imagePreview})` }}
                    ></div>
                  </div>
                </div>
                {formik.touched.thumbnail && formik.errors.thumbnail && (
                  <p className="error-text">{formik.errors.thumbnail}</p>
                )}
              </div>
            </div>

            <label htmlFor="description" className="description">
              Description
            </label>
            <br />
            <textarea
              id="description"
              name="description"
              placeholder="Type here..."
              rows="5"
              value={formik.values.description}
              onChange={formik.handleChange}
            ></textarea>
          </div>

          <div className="formSection">
            <div className="select-container">
              <div className="form">
                <label>Select Choices</label>

                <select
                  name="category"
                  id="category"
                  value={String(formik.values.category || "")}
                  onChange={formik.handleChange}
                >
                  <option value="">---Category---</option>
                  {categoryOptions.map((option) => {
                    const optionId = String(option._id || option.id || "");
                    return (
                      <option key={optionId} value={optionId}>
                        {option.title || option.name}
                      </option>
                    );
                  })}
                </select>
                <select
                  name="industry"
                  id="industry"
                  value={String(formik.values.industries[0] || "")}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(
                      "industries",
                      value ? [value] : [],
                    );
                  }}
                >
                  <option value="">---Industry---</option>
                  {industryOptions.map((option) => {
                    const optionId = String(option._id || option.id || "");
                    return (
                      <option key={optionId} value={optionId}>
                        {option.title || option.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
          <div className="formSection">
            <div className="modal-tags-container">
              <div style={{ marginBottom: "20px" }}>
                <label htmlFor="tagInput" style={{ textAlign: "left" }}>
                  Select Tags
                </label>

                <div className="tag-selector-wrapper">
                  <div className="tag-dropdown-wrapper" ref={tagDropdownRef}>
                    <div
                      className="tag-dropdown-select"
                      onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
                    >
                      <span>
                        {tags.length > 0
                          ? `${tags.length} tag(s) selected`
                          : "Select tags..."}
                      </span>
                      <span className="dropdown-arrow">
                        {isTagDropdownOpen ? "▲" : "▼"}
                      </span>
                    </div>

                    {isTagDropdownOpen && (
                      <div className="tag-dropdown-menu">
                        {availableTags.map((tag) => {
                          const tagName =
                            typeof tag === "string"
                              ? tag
                              : tag.title || tag.name;
                          return (
                            <label
                              key={tag._id || tag.id || tagName}
                              className="tag-dropdown-item"
                            >
                              <input
                                type="checkbox"
                                checked={isTagSelected(tag._id || tag.id)}
                                onChange={() =>
                                  handleToggleTag(tag._id || tag.id)
                                }
                              />
                              <span>{tagName}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {tags.length > 0 && (
                    <div className="tags-inline">
                      <ul className="tagLists">
                        {tags.map((tag, index) => {
                          const label = getTagLabel(tag);
                          if (!label) return null;

                          return (
                          <li key={index}>
                            <TiTick style={{ marginRight: "5px" }} />
                            {label}
                            <button
                              className="remove-tag"
                              aria-label="Remove tag"
                              onClick={() => handleRemoveTag(tag)}
                              type="button"
                            >
                              ×
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="formSection">
          <div className="UploadImages">
            <div className="bulkThumbnail">
              <label>
                Bulk Image Upload (
                {formatPlanCap(bulkImages.length, planLimits.maxProductImages)})
              </label>
              <div
                className={`bulkUpload ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: isDragging
                    ? "2px dashed #0A4DA2"
                    : "2px dashed transparent",
                  backgroundColor: isDragging ? "#f0f7ff" : "transparent",
                  transition: "all 0.3s ease",
                }}
              >
                <div className="bulk-images-grid">
                  {bulkImages.map((img, index) => (
                    <div key={index} className="bulk-image-card">
                      <img
                        src={img}
                        alt={`upload-${index}`}
                        onClick={() => handleImageClick(img, index)}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  ))}
                  <div
                    className="add-new-card"
                    onClick={() => {
                      if (
                        Number.isFinite(planLimits.maxProductImages) &&
                        bulkImageFiles.length >= planLimits.maxProductImages
                      ) {
                        toast.error(
                          `Your plan allows ${planLimits.maxProductImages} image(s) per product.`,
                        );
                        return;
                      }
                      document.getElementById("bulkImageInput").click();
                    }}
                  >
                    <span
                      style={{
                        textAlign: "center",
                        fontSize: "clamp(0.5rem, 2.5vw, 0.8rem)",
                      }}
                    >
                      Drag & Drop or Click to Upload
                    </span>
                  </div>
                </div>
                <input
                  type="file"
                  multiple
                  id="bulkImageInput"
                  accept="image/jpeg,image/jpg,image/png"
                  style={{ display: "none" }}
                  onChange={handleBulkImageChange}
                  onBlur={() => formik.setFieldTouched("bulkImages", true)}
                />
                {formik.touched.bulkImages && formik.errors.bulkImages && (
                  <p className="error-text">{formik.errors.bulkImages}</p>
                )}
                {/* <button className="addBtn" onClick={uploadImages}>
                  {" "}
                  <IoCloudUploadOutline style={{ marginRight: "8px" }} /> Upload
                </button> */}
              </div>
            </div>
          </div>

          <div className="fileUpload">
            <div className="upload-header">
              <label>
                Upload File (
                {formatPlanCap(
                  existingCatalogs.length + newCatalogFiles.length,
                  planLimits.maxProductCatalogs,
                )}
                )
              </label>
            </div>

            <div className="upArea">
              <div
                className="upload-area"
                onClick={() => {
                  if (
                    Number.isFinite(planLimits.maxProductCatalogs) &&
                    existingCatalogs.length + newCatalogFiles.length >=
                      planLimits.maxProductCatalogs
                  ) {
                    toast.error(
                      `Your plan allows ${planLimits.maxProductCatalogs} catalogue(s) per product.`,
                    );
                    return;
                  }
                  document.getElementById("fileInput").click();
                }}
                onDragOver={handleFileDragOver}
                onDragLeave={handleFileDragLeave}
                onDrop={handleFileDrop}
                style={{
                  border: isFileDragging
                    ? "2px dashed #0A4DA2"
                    : "2px dashed #ddd",
                  backgroundColor: isFileDragging ? "#f0f7ff" : "transparent",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              >
                <div className="plus-icon">+</div>
                <p>Click or drag files to upload</p>
                <p className="file-info">
                  It must be .pdf or .docs file (
                  {Number.isFinite(planLimits.maxProductCatalogSizeMB)
                    ? `${planLimits.maxProductCatalogSizeMB}MB`
                    : "20MB"}{" "}
                  Max)
                </p>
              </div>

              <input
                type="file"
                id="fileInput"
                accept=".pdf,.doc,.docx"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {uploadedFiles.length > 0 && (
                <div className="file-list">
                  {uploadedFiles.map((file, index) => (
                    <div key={file.id} className="file-item">
                      <div className="file-info-row">
                        <div className="file-icon">📄</div>
                        <div className="file-details">
                          <div className="file-name">{file.name}</div>
                          <div className="file-meta">
                            <span className="file-size">{file.size} mb</span>
                            {file.status === "uploading" && (
                              <>
                                <span className="file-progress">
                                  {file.progress}%
                                </span>
                                <span className="file-time">
                                  {file.timeLeft} sec left
                                </span>
                              </>
                            )}
                            {file.status === "uploaded" && (
                              <span className="file-status-text">Uploaded</span>
                            )}
                            {file.status === "failed" && (
                              <span className="file-status-text error">
                                Upload Failed
                              </span>
                            )}
                          </div>
                          {file.status === "uploading" && (
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${file.progress}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="file-actions">
                        {file.status === "failed" && (
                          <button
                            className="retry-btn"
                            onClick={() => handleRetryFile(file.id)}
                          >
                            ↻
                          </button>
                        )}
                        <button
                          className="delete-btn"
                          onClick={() => handleRemoveFile(file.id, index)}
                        >
                          <RiDeleteBin5Line />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* <div className="button-group">
              <button className="cancel-btn">Cancel</button>
              <button className="save-btn" onClick={uploadImages}>
                Save
              </button>
            </div> */}
          </div>
          <div className="videoContainer">
            <label>
              Product Video Upload (
              {formatPlanCap(bulkVideos.length, planLimits.maxProductVideos)})
            </label>
            <div className="bulkUpload">
              <div className="url-input-container">
                <input
                  type="text"
                  placeholder="Enter Video URL"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleAddVideoUrl}
                  className="CommonButton"
                >
                  Add URL
                </button>
              </div>
              <div
                className="bulk-video-grid"
                style={{
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                }}
              >
                {bulkVideos.map((vid, index) => {
                  const embedUrl = getEmbedUrl(vid);
                  const meta = bulkVideoFiles[index] || {};

                  return (
                    <div
                      key={index}
                      className="video-manage-wrapper"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <div
                        className="bulk-video-card"
                        style={{
                          position: "relative",
                          background: "#fff",
                          borderRadius: "16px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          border: "1px solid #eee",
                          height: "200px",
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleDeleteVideo(index)}
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                            background: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#d11a2a",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            zIndex: 10,
                          }}
                          title="Remove video"
                        >
                          <RiDeleteBin5Line size={18} />
                        </button>

                        <div
                          className="video-preview-container"
                          style={{ width: "100%", height: "100%" }}
                        >
                          {embedUrl ? (
                            <iframe
                              width="100%"
                              height="100%"
                              src={embedUrl}
                              title={`video-${index}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{ pointerEvents: "none" }}
                            ></iframe>
                          ) : (
                            <video
                              width="100%"
                              height="100%"
                              key={vid}
                              style={{
                                pointerEvents: "none",
                                objectFit: "cover",
                              }}
                            >
                              <source src={vid} type="video/mp4" />
                            </video>
                          )}
                          <div
                            onClick={() => handleVideoClick(vid, index)}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              cursor: "pointer",
                              backgroundColor: "transparent",
                            }}
                            title="Click to preview"
                          ></div>
                        </div>
                      </div>

                      <div
                        className="video-meta-inputs"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Video Title"
                          value={meta.title || ""}
                          onChange={(e) =>
                            handleVideoMetaChange(
                              index,
                              "title",
                              e.target.value,
                            )
                          }
                          style={{
                            fontSize: "14px",
                            padding: "12px 15px",
                            border: "1px solid #ddd",
                            borderRadius: "12px",
                            background: "#fff",
                            width: "100%",
                            boxSizing: "border-box",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                          }}
                        />
                        <textarea
                          placeholder="Video Description"
                          value={meta.description || ""}
                          onChange={(e) =>
                            handleVideoMetaChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          style={{
                            fontSize: "13px",
                            padding: "12px 15px",
                            border: "1px solid #ddd",
                            borderRadius: "12px",
                            background: "#fff",
                            width: "100%",
                            minHeight: "80px",
                            resize: "vertical",
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <input
                type="file"
                multiple
                id="bulkVideoInput"
                style={{ display: "none" }}
                onChange={handleBulkVideoChange}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <button
            type="button"
            className="CancelButton"
            onClick={() => navigate("/products")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="CommonButton"
            disabled={
              isCreating ||
              isUpdating ||
              (!isEditMode && !planLimits.products.canCreate)
            }
          >
            {isCreating || isUpdating
              ? "Saving..."
              : isEditMode
                ? "Update Product"
                : "Create Product"}
          </button>
        </div>
        </form>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        className="image-preview-modal"
      >
        <div className="image-modal-wrapper">
          <img
            src={selectedImage}
            alt="Preview"
            className="modal-preview-image"
          />
          <button
            className="modal-delete-btn"
            onClick={() => {
              handleDeleteImage(selectedImageIndex);
              closeModal();
            }}
            title="Delete image"
          >
            <RiDeleteBin5Line />
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isVideoModalOpen}
        onClose={closeVideoModal}
        className="video-preview-modal"
      >
        <div className="video-modal-wrapper" style={{ position: "relative" }}>
          <button
            className="modal-delete-btn"
            onClick={() => {
              handleDeleteVideo(selectedVideoIndex);
              closeVideoModal();
            }}
            title="Delete video"
            style={{
              position: "absolute",
              right: "10px",
              zIndex: 10,
            }}
          >
            <RiDeleteBin5Line />
          </button>
          {selectedVideo &&
            (() => {
              const embedUrl = getEmbedUrl(selectedVideo);
              return embedUrl ? (
                <iframe
                  width="100%"
                  height="500px"
                  src={embedUrl}
                  title="Video Preview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video width="100%" height="500px" controls>
                  <source src={selectedVideo} type="video/mp4" />
                </video>
              );
            })()}
        </div>
      </Modal>
    </>
  );
};

export default AddProducts;
